/**
 * Auth Service
 * API calls för autentisering
 */

import api from '@/services/api/client';
import { AxiosResponse } from 'axios';

export interface LoginRequestType {
  email: string;
  password?: string;
  preferPassword?: boolean;
}

export interface SignupRequestType {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  tos_accept: boolean;
}

export interface AuthData {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    organization_id?: string;
    preferred_language?: string;
  };
}

export interface SignupSuccessData {
  email: string;
  requiresEmailVerification: boolean;
  organizationLinkedByDomain?: boolean;
}

export interface LoginResponse {
  success: boolean;
  data:
    | AuthData
    | { email: string; authMethodChanged?: boolean }
    | { requiresSaml: boolean; organizationId: string }
    | { requiresOidc: boolean; organizationId: string }
    | { requiresEmailVerification: boolean; email?: string }
    | SignupSuccessData;
  message?: string;
  error?: string;
}

/**
 * Login request
 */
export const loginRequest = async (
  data: LoginRequestType
): Promise<AxiosResponse<LoginResponse>> => {
  try {
    const response = await api.post<LoginResponse>('/api/auth/login', data);
    return response;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Signup request
 */
export const signupRequest = async (
  data: SignupRequestType
): Promise<AxiosResponse<LoginResponse>> => {
  try {
    const response = await api.post<LoginResponse>('/api/auth/signup', data);
    return response;
  } catch (error) {
    console.error('Signup error:', error);
    throw error;
  }
};

/**
 * Reset password request
 */
export const resetPasswordRequest = async (email: string): Promise<AxiosResponse> => {
  try {
    const response = await api.post('/api/auth/reset-password', { email });
    return response;
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

export type OtpType = 'LOGIN' | 'REGISTER';

export interface VerifyOtpRequestType {
  email: string;
  otp: string;
  type: OtpType;
}

export interface ResendOtpRequestType {
  email: string;
  type: OtpType;
}

export interface LoginResponseWithEmail {
  success: boolean;
  data: {
    email: string;
  };
  message?: string;
}

/**
 * Verify OTP request
 */
export const verifyOtpRequest = async (
  data: VerifyOtpRequestType
): Promise<AxiosResponse<LoginResponse>> => {
  try {
    const response = await api.post<LoginResponse>('/api/auth/verify-otp', data);
    return response;
  } catch (error) {
    console.error('Verify OTP error:', error);
    throw error;
  }
};

/**
 * Resend OTP request
 */
export const resendOtpRequest = async (
  data: ResendOtpRequestType
): Promise<AxiosResponse> => {
  try {
    const response = await api.post('/api/auth/resend-otp', data);
    return response;
  } catch (error) {
    console.error('Resend OTP error:', error);
    throw error;
  }
};

/**
 * Resend verification email request
 */
export const resendVerificationEmailRequest = async (
  email: string
): Promise<AxiosResponse> => {
  try {
    const response = await api.post('/api/auth/resend-verification-email', { email });
    return response;
  } catch (error) {
    console.error('Resend verification email error:', error);
    throw error;
  }
};